import React from 'react'
import { useForm, Controller, FormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { Box, Grid, TextField, Button } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { reverse } from 'named-urls'

import useSnackbar from 'hooks/useSnackbar'

import { Card, AutocompleteTypeAndSearch } from 'components'
import { AttachmentForm, CycleTable } from './components'

import { routes } from 'Routes'
import * as service from 'service'
import helpers from 'helpers'
import schema from './schema'

const PdcaForm = ({ mode, setLoading, refresh, dataPdca }) => {
  const history = useHistory()
  const snackbar = useSnackbar()

  const nameMode =
    mode === 'new' ? 'Criação' : mode === 'edit' ? 'Edição' : 'Visualização'
  const isDisbled = mode === 'show'
  const isNew = mode === 'new'
  const modeRequest = mode === 'new' ? 'create' : 'update'

  const handleReturn = () => history.push(routes.pdca.all)
  const handleRedirectEdit = (pdcaId) =>
    history.push(reverse(routes.pdca.edit, { pdcaId }))

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      name: dataPdca?.name || '',
      observation: dataPdca?.observation || '',
      origin: dataPdca?.origin || '',
      department: dataPdca?.department || null,
      responsibleUser: dataPdca?.responsibleUser || null,
      startDate: dataPdca?.startDate ? new Date(dataPdca?.startDate) : null,
      endDate: dataPdca?.endDate ? new Date(dataPdca?.endDate) : null,
    },
  })

  const { control, errors, handleSubmit } = formMethods

  const formattedData = (data) => ({
    ...data,
    startDate: data?.startDate ? data.startDate.toISOString() : null,
    endDate: data?.endDate ? data.endDate.toISOString() : null,
    departmentId: data?.department?.id,
    responsibleUserId: data?.responsibleUser?.id,
  })

  const onSubmit = async (data) => {
    if (isDisbled) return null
    setLoading(true)
    try {
      const response = await service.dponet.pdca[modeRequest]({
        pdcaId: dataPdca?.id,
        ...formattedData(data),
      })

      snackbar.open({
        variant: 'success',
        message: isNew
          ? 'PDCA criado com sucesso.'
          : 'PDCA editado com sucesso.',
      })

      if (isNew) {
        if (response?.data) {
          handleRedirectEdit(response?.data?.monitoringManagementPdca?.id)
        }
      } else {
        return refresh()
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          `Falha ao ${isNew ? 'criar' : 'editar'} PDCA.`,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchDepartments = async (inputValue) => {
    try {
      const response = await service.dponet.departments.get({
        name: inputValue,
        perPage: 10000000,
        minimal: true,
        order: 'name ASC',
      })

      return response?.data?.departments || []
    } catch {
      return []
    }
  }

  const fetchUsers = async (inputValue) => {
    try {
      const response = await service.dponet.users.get({
        name: inputValue,
        perPage: 10000000,
        minimal: true,
        order: 'name ASC',
      })

      return response?.data?.users || []
    } catch {
      return []
    }
  }

  return (
    <>
      <Card title={`${nameMode} do PDCA`}>
        <FormContext {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Controller
                  control={control}
                  as={
                    <TextField
                      label="Nome *"
                      type="text"
                      color="primary"
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors?.name?.message}
                      fullWidth
                      disabled={isDisbled}
                    />
                  }
                  name="name"
                  mode="onBlur"
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteTypeAndSearch
                  textField={{
                    label: 'Departamentos *',
                    variant: 'outlined',
                    error: !!errors?.department,
                    helperText: errors?.department?.message,
                    disabled: isDisbled,
                  }}
                  minCharsToSearch={2}
                  requestToLoadOptions={fetchDepartments}
                  name="department"
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteTypeAndSearch
                  textField={{
                    label: 'Responsável *',
                    variant: 'outlined',
                    error: !!errors?.responsibleUser,
                    helperText: errors?.responsibleUser?.message,
                    disabled: isDisbled,
                  }}
                  requestToLoadOptions={fetchUsers}
                  name="responsibleUser"
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  control={control}
                  as={
                    <TextField
                      label="Origem *"
                      type="text"
                      color="primary"
                      variant="outlined"
                      error={!!errors.origin}
                      helperText={errors?.origin?.message}
                      fullWidth
                      disabled={isDisbled}
                    />
                  }
                  name="origin"
                  mode="onBlur"
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      format="DD/MM/yyyy"
                      label="Data de Início *"
                      helperText={errors?.startDate?.message}
                      error={!!errors.startDate}
                      inputVariant="outlined"
                      onChange={(newValue) => ({ value: newValue })}
                      disabled={isDisbled}
                    />
                  }
                  control={control}
                  defaultValue={null}
                  name="startDate"
                  mode="onChange"
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      format="DD/MM/yyyy"
                      label="Data de término prevista *"
                      helperText={errors?.endDate?.message}
                      error={!!errors.endDate}
                      inputVariant="outlined"
                      onChange={(newValue) => ({ value: newValue })}
                      disabled={isDisbled}
                    />
                  }
                  control={control}
                  defaultValue={null}
                  name="endDate"
                  mode="onChange"
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  as={
                    <TextField
                      label="Observação *"
                      type="text"
                      color="primary"
                      variant="outlined"
                      error={!!errors.observation}
                      helperText={errors?.observation?.message}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={3}
                      disabled={isDisbled}
                    />
                  }
                  name="observation"
                  mode="onBlur"
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Box pr={1}>
                <Button type="button" variant="outlined" onClick={handleReturn}>
                  Voltar
                </Button>
              </Box>
              {!isDisbled && (
                <Box>
                  <Button color="primary" variant="contained" type="submit">
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          </form>
        </FormContext>
      </Card>
      {!isNew && (
        <>
          <AttachmentForm
            isDisbled={isDisbled}
            setLoading={setLoading}
            dataPdca={dataPdca}
            refresh={refresh}
          />
          {dataPdca && (
            <CycleTable pdcaId={dataPdca?.id} isDisbled={isDisbled} />
          )}
        </>
      )}
    </>
  )
}

export default PdcaForm
